// ============================================================
// CONTACTS - what the party knows of the figures it cannot see (plan
// map-display, H; the rulings of 2026-10-07).
//
// Every hidden figure (engine/roster/hidden) has ONE level, kept on the
// GM's desk and set by the GM alone:
//   unknown  - nothing, anywhere (the default: a figure the desk has never
//              heard of is unknown);
//   signs    - a slow ring on the tracker for each figure at Signs, up to
//              five (2026-10-21: "a ring for each, up to a number");
//   contact  - a dot on the tracker, on its bearing from the party, and a
//              "?" on the map where it was last seen, with a short trail.
// Revealed is the reveal itself (the figure is no longer hidden), and a
// revealed (or parked) figure's record is forgotten: hidden again, it starts
// unknown.
//
// The GM's screen turns the desk into what players may know
// (`publicContacts`) and publishes that on the phase frame. A contact is
// named there by an OPAQUE id (`c1`, `c2`, ... never reused), never by the
// figure's id or its alias: the published record must not let a player
// join a contact to an "Unknown N" line in the log.
//
// Pure. Zero React dependency.
// ============================================================
import { bearingBetween, type GroundPoint } from '../battlefield/facing';
import { canonicalJson } from '../canonicalJson';

export type ContactLevel = 'unknown' | 'signs' | 'contact';
const LEVELS: readonly ContactLevel[] = ['unknown', 'signs', 'contact'];

interface ContactRecord {
  level: ContactLevel;
  /** The opaque id players know it by; given the first time it becomes a contact, and kept. */
  cid?: string;
  /** Where it was last seen, when it is (or was) a contact. */
  lastSeen?: GroundPoint;
  /** The sightings before the last, oldest first, at most `TRAIL`. */
  trail?: GroundPoint[];
}

/** The GM's desk record: the levels by figure id, and the next opaque id. */
export interface ContactDesk {
  next: number;
  figures: Record<string, ContactRecord>;
}

/** What players are told: whether there are signs, the contacts' marks, and the tracker's bearings. */
export interface PublicContacts {
  /** Any at Signs: kept for a tab still on an older version, which draws one ring. */
  signs: boolean;
  /** How many slow rings the tracker draws: the figures at Signs, capped at `SIGN_RINGS`. */
  signCount: number;
  contacts: { cid: string; lastSeen: GroundPoint; trail: GroundPoint[] }[];
  /** Contacts merged by compass bearing from the party: one dot per bearing, sized by the count. */
  bearings: { compass: Compass; count: number }[];
}

/** How many sightings a trail keeps behind the last. */
const TRAIL = 4;

/** The most Signs rings the tracker draws (the user, 2026-10-21: "up to a number"); more are still five. */
const SIGN_RINGS = 5;

/**
 * Eight ways round, in world space: 0 degrees is due east and +z is south (engine/battlefield/facing), so the
 * names run clockwise from east.
 */
export const COMPASS = [
  'east',
  'south-east',
  'south',
  'south-west',
  'west',
  'north-west',
  'north',
  'north-east',
] as const;
export type Compass = (typeof COMPASS)[number];

export const compassOf = (degrees: number): Compass => COMPASS[Math.round(degrees / 45) % 8];

/**
 * Set a figure's level. Becoming (or staying) a contact `at` a point records the sighting: the last one joins
 * the trail. A contact keeps its opaque id through every level; a new contact takes the desk's next.
 */
export function setContactLevel(desk: ContactDesk, id: string, level: ContactLevel, at?: GroundPoint): ContactDesk {
  const was = desk.figures[id];
  const needsId = level === 'contact' && !was?.cid;
  const record: ContactRecord = { ...was, level, ...(needsId ? { cid: `c${desk.next}` } : {}) };
  if (level === 'contact' && at) sighted(record, was, at);
  return { next: desk.next + (needsId ? 1 : 0), figures: { ...desk.figures, [id]: record } };
}

/** Set many figures' levels at once (the GM's "All Unknown to Signs"), each seen at its point when it has one. */
export function withLevels(
  desk: ContactDesk,
  ids: readonly string[],
  level: ContactLevel,
  points: Readonly<Record<string, GroundPoint>> = {},
): ContactDesk {
  return ids.reduce((d, id) => setContactLevel(d, id, level, points[id]), desk);
}

/** Forget figures (revealed, parked, removed): hidden again they start unknown. The opaque ids stay used. */
export function forgetFigures(desk: ContactDesk, ids: readonly string[]): ContactDesk {
  const gone = ids.filter((id) => id in desk.figures);
  if (!gone.length) return desk;
  const figures = { ...desk.figures };
  for (const id of gone) delete figures[id];
  return { ...desk, figures };
}

/** A sighting on `record`: the last one (if any) onto the trail, capped. */
function sighted(record: ContactRecord, was: ContactRecord | undefined, at: GroundPoint): void {
  const trail = was?.lastSeen ? [...(was.trail ?? []), was.lastSeen] : (was?.trail ?? []);
  record.trail = trail.slice(-TRAIL);
  record.lastSeen = { x: at.x, z: at.z };
}

/**
 * What the players may know. `positions` are the hidden figures' tokens on the GM's screen (where the tracker
 * reads them; a contact off the map is read where it was last seen), `party` the party's tokens: bearings are
 * taken from their middle. Nothing here names a figure.
 */
export function publicContacts(
  desk: ContactDesk,
  positions: Readonly<Record<string, GroundPoint>>,
  party: readonly GroundPoint[],
): PublicContacts {
  const entries = Object.entries(desk.figures);
  // A contact made before its figure had a token is seen where it stands once it has one.
  const contacts = entries.flatMap(([id, r]) => {
    const seen = r.lastSeen ?? positions[id];
    return r.level === 'contact' && r.cid && seen ? [{ id, cid: r.cid, seen, trail: r.trail ?? [] }] : [];
  });
  const signCount = entries.filter(([, r]) => r.level === 'signs').length;
  return {
    signs: signCount > 0,
    signCount: Math.min(SIGN_RINGS, signCount),
    contacts: contacts.map((c) => ({ cid: c.cid, lastSeen: c.seen, trail: c.trail })),
    bearings: bearingsOf(
      contacts.map((c) => positions[c.id] ?? c.seen),
      party,
    ),
  };
}

/** The party's middle, where every bearing is taken from. */
const middleOf = (party: readonly GroundPoint[]): GroundPoint => ({
  x: mean(party.map((p) => p.x)),
  z: mean(party.map((p) => p.z)),
});

/** A point's compass bearing from the party's middle; null with no party, or nowhere to point at. */
export function compassFrom(party: readonly GroundPoint[], point: GroundPoint | undefined): Compass | null {
  return party.length && point ? compassOf(bearingBetween(middleOf(party), point)) : null;
}

/** The contacts' compass bearings from the party's middle, merged and counted, in compass order. */
function bearingsOf(points: GroundPoint[], party: readonly GroundPoint[]): PublicContacts['bearings'] {
  if (!party.length || !points.length) return [];
  const counts = new Map<Compass, number>();
  for (const p of points) {
    const c = compassFrom(party, p)!;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return COMPASS.filter((c) => counts.has(c)).map((compass) => ({ compass, count: counts.get(compass)! }));
}

const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

/** A stored desk, read field by field: an unrecognised record is dropped, rubbish is an empty desk. */
export function readContactDesk(raw: unknown): ContactDesk {
  const stored = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const next = Number.isInteger(stored.next) && (stored.next as number) > 0 ? (stored.next as number) : 1;
  const figures: Record<string, ContactRecord> = {};
  for (const [id, r] of Object.entries((stored.figures ?? {}) as Record<string, unknown>)) {
    const record = readRecord(r);
    if (record) figures[id] = record;
  }
  return { next, figures };
}

function readRecord(raw: unknown): ContactRecord | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (!LEVELS.includes(r.level as ContactLevel)) return null;
  const lastSeen = readAt(r.lastSeen);
  return {
    level: r.level as ContactLevel,
    ...(typeof r.cid === 'string' ? { cid: r.cid } : {}),
    ...(lastSeen ? { lastSeen } : {}),
    ...(Array.isArray(r.trail)
      ? {
          trail: r.trail
            .map(readAt)
            .filter((p): p is GroundPoint => !!p)
            .slice(-TRAIL),
        }
      : {}),
  };
}

function readAt(raw: unknown): GroundPoint | null {
  const p = (raw ?? {}) as Record<string, unknown>;
  return Number.isFinite(p.x) && Number.isFinite(p.z) ? { x: p.x as number, z: p.z as number } : null;
}

const EMPTY: PublicContacts = { signs: false, signCount: 0, contacts: [], bearings: [] };

/** The rings a frame asks for: its count, capped; a publisher from before the count drew one for its boolean. */
function readSignCount(r: Record<string, unknown>): number {
  const n = r.signCount;
  if (Number.isInteger(n) && (n as number) >= 0) return Math.min(SIGN_RINGS, n as number);
  return r.signs === true ? 1 : 0;
}

/**
 * What a player's screen reads off the frame: validated field by field (a contact with no id or no sighting is
 * dropped, a bearing that names no compass point too). Absent is nothing known.
 */
export function readPublicContacts(raw: unknown): PublicContacts {
  if (!raw || typeof raw !== 'object') return EMPTY;
  const r = raw as Record<string, unknown>;
  return {
    signs: r.signs === true,
    signCount: readSignCount(r),
    contacts: (Array.isArray(r.contacts) ? r.contacts : []).flatMap(readPublicContact),
    bearings: (Array.isArray(r.bearings) ? r.bearings : []).flatMap(readBearing),
  };
}

function readPublicContact(raw: unknown): PublicContacts['contacts'] {
  const c = (raw ?? {}) as Record<string, unknown>;
  const lastSeen = readAt(c.lastSeen);
  if (typeof c.cid !== 'string' || !lastSeen) return [];
  const trail = (Array.isArray(c.trail) ? c.trail : []).map(readAt).filter((p): p is GroundPoint => !!p);
  return [{ cid: c.cid, lastSeen, trail: trail.slice(-TRAIL) }];
}

function readBearing(raw: unknown): PublicContacts['bearings'] {
  const b = (raw ?? {}) as Record<string, unknown>;
  const ok = COMPASS.includes(b.compass as Compass) && Number.isInteger(b.count) && (b.count as number) > 0;
  return ok ? [{ compass: b.compass as Compass, count: b.count as number }] : [];
}

/** Whether two published records say the same: the GM's screen writes only a change. */
export const sameContacts = (a: PublicContacts | undefined, b: PublicContacts): boolean =>
  canonicalJson(a ?? EMPTY) === canonicalJson(b);
